import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Property } from '../services/api';

/**
 * BottomNavigation — 모바일 하단 5탭 (v2 Stitch, 2026-09-21 대표 결정 1).
 *
 * 탭: 홈(/) · 분양목록(/list) · 지도(/map) · 관심(/profile?tab=favorites) · 마이(/profile)
 * 목업: docs/design-v2 .tabbar — 높이 64, 활성 #FF6B00(text-accent) / 비활성 #94A3B8(text-mute2)
 * - 데스크탑 (md+) 은 DesktopNav(GNB) 가 헤더에 있어 hidden
 * - 구 '전체' 메뉴 시트(이용약관·개인정보처리방침·광고제휴)는 App 하단 푸터 링크로 이동
 *
 * Props
 *   - onMyLocationClick 등은 BC 위해 유지하되 미사용 (내위치는 지도 화면 우측 컨트롤이 대체)
 */

interface BottomNavigationProps {
  /** @deprecated v2 5탭 전환(2026-09-21) — '내위치' 탭 제거, 지도 화면 컨트롤이 대체 */
  onMyLocationClick?: () => void;
  /** @deprecated 모바일 시트 폐기 (2026-05-19) — 리스트 버튼은 /list 로 직접 이동 */
  onListToggle?: () => void;
  /** @deprecated 미사용 */
  showList?: boolean;
  /** @deprecated 미사용 */
  specialProperties?: Property[];
}

const ICON = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const BottomNavigation: React.FC<BottomNavigationProps> = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const isFavTab = pathname === '/profile' && new URLSearchParams(search).get('tab') === 'favorites';

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-[64px] items-center justify-around backdrop-blur-md md:hidden"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        backgroundColor: 'rgba(255,255,255,0.97)',
        boxShadow: '0 -2px 12px rgba(15,23,42,.06)',
      }}
      aria-label="하단 네비게이션"
    >
      <NavItem
        to="/"
        label="홈"
        active={pathname === '/'}
        icon={
          <svg {...ICON}>
            <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
          </svg>
        }
      />
      <NavItem
        to="/list"
        label="분양목록"
        active={pathname.startsWith('/list')}
        icon={
          <svg {...ICON}>
            <line x1="9" y1="6" x2="20" y2="6" />
            <line x1="9" y1="12" x2="20" y2="12" />
            <line x1="9" y1="18" x2="20" y2="18" />
            <circle cx="4.5" cy="6" r="1" />
            <circle cx="4.5" cy="12" r="1" />
            <circle cx="4.5" cy="18" r="1" />
          </svg>
        }
      />
      <NavItem
        to="/map"
        label="지도"
        active={pathname.startsWith('/map')}
        icon={
          <svg {...ICON}>
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" />
            <line x1="16" y1="6" x2="16" y2="22" />
          </svg>
        }
      />
      <NavItem
        to="/profile?tab=favorites"
        label="관심"
        active={isFavTab}
        icon={
          <svg {...ICON}>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        }
      />
      <NavItem
        to="/profile"
        label="마이"
        active={pathname.startsWith('/profile') && !isFavTab}
        icon={
          <svg {...ICON}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        }
      />
    </nav>
  );
};

// ─────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────
interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
}
const NavItem: React.FC<NavItemProps> = ({ to, label, icon, active }) => (
  <Link
    to={to}
    className={`flex min-w-[56px] flex-1 flex-col items-center justify-center gap-[3px] text-[11px] transition-colors ${
      active ? 'font-bold text-accent' : 'font-semibold text-mute2 hover:text-body'
    }`}
    aria-current={active ? 'page' : undefined}
  >
    {icon}
    <span>{label}</span>
  </Link>
);

export default BottomNavigation;
